import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionFull, ModifyAction, PostAction } from 'interfaces/action.interface';
import { dayjs, RootState, ShortUser } from '@umanforge/common';
import { ActionService } from 'services/action.service';
import { useDispatch, useSelector } from 'react-redux';
import { TaskFull } from 'interfaces/task.interface';
import { ModifyNote, Note, PostNote } from 'interfaces/note.interface';
import { Image } from 'interfaces/image.interface';
import { NoteService } from 'services/note.service';


interface ActionErrors {
  dateStart: string | undefined,
}

const useActionTile = (
  action: ActionFull | undefined,
  user: ShortUser,
  task: TaskFull,
  disable?: boolean,
  onChange?: () => void
) => {
  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );

  const [ dateStart, setDateStart ] = useState<dayjs.Dayjs>( action?.dateStart ? dayjs( action?.dateStart ) : dayjs( task.dateStart ) );
  const [ dateEnd, setDateEnd ] = useState<dayjs.Dayjs>( action?.dateEnd ? dayjs( action?.dateEnd ) : dayjs( task.dateEnd ) );

  const [ timeStart, setTimeStart ] = useState<dayjs.Dayjs>( action?.dateStart ? dayjs( action?.dateStart ) : dayjs( task.dateStart ) );
  const [ timeEnd, setTimeEnd ] = useState<dayjs.Dayjs | undefined>(  action?.dateEnd ? dayjs( action?.dateEnd ) : undefined );

  const [ notes, setNotes ] = useState<Note[]>( action?.notes ?? [] );
  const [ notesSave, ] = useState<Note[]>( action?.notes ?? [] );
  const [ images, setImages ] = useState<Image[]>( action?.images ?? [] );

  const [ cannotAchieve, setCannotAchieve ] = useState<boolean>( action?.cannotAchieve ?? false );
  const [ colorStatus, setColorStatus ] = useState( 'bg-gray-100' );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isFormValid, setIsFormValid ] = useState( false );
  const [ isFormChanged, setIsFormChanged ] = useState( false );

  const [ errors, setErrors ] = useState<ActionErrors>( {
    dateStart: undefined
  } );

  useEffect( () => {
    if ( isFirstRender ) {
      init();
    }
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const isValid = validateForm();
      const isChanged = checkFormChanged();

      setIsFormValid( isValid && isChanged );
      setIsFormChanged( isChanged );

      if ( !action ) {
        setColorStatus( 'bg-gray-50' );
      }
      else if ( action?.cannotAchieve ) {
        setColorStatus( 'bg-red-100' );
      }
      else if ( action.dateEnd ) {
        setColorStatus( 'bg-green-100' );
      }
      else {
        setColorStatus( 'bg-orange-50' );
      }
    }
  }, [
    dateStart,
    dateEnd,
    timeStart,
    timeEnd,
    notes,
    cannotAchieve,
    isFirstRender,
    action
  ] );

  const init = () => {
    setIsFirstRender( false );
  };

  const dateAndTimeToISO = ( date: dayjs.Dayjs, time: dayjs.Dayjs ): string => {
    return date.startOf( 'day' ).add( time.hour(), 'hour' ).add( time.minute(), 'minute' ).toISOString();
  };

  const checkFormChanged = (): boolean => {
    return action ? checkEditModeChanged() : true;
  };

  const validateForm = (): boolean => {
    const newErrors: ActionErrors = {
      dateStart: undefined
    };

    if ( timeEnd ) {
      const start = dateAndTimeToISO( dateStart, timeStart );
      const end = dateAndTimeToISO( dateEnd, timeEnd );

      if ( dayjs( end ).diff( dayjs( start ) ) < 0 ) {
        newErrors.dateStart = t( 'translation:errors.action_start_after_end' );
      }
      setErrors( newErrors );
    }
    return (
      !newErrors.dateStart
    );
  };

  const checkEditModeChanged = () => {
    return (
      action?.dateStart != dateAndTimeToISO( dateStart, timeStart ) ||
      ( timeEnd != undefined && ( action?.dateEnd != dateAndTimeToISO( dateEnd, timeEnd ) ) ) ||

      (
        action.notes.sort().map( note => { return note.id + '-' + note.content + '-' + ( ( note.disabled && note.disabled !== true ) ? 'true' : 'false' ); } ).join( '_' )
        !=
        notes.sort().map( note => { return note.id + '-' + note.content + '-' + ( ( note.disabled ) ? 'true' : 'false' ); } ).join( '_' )
      ) ||


      ( action?.cannotAchieve ? true : false ) != ( cannotAchieve ? true : false ) // Tenary pour comparer meme si null ou undefined
    );
  };

  const handleValidateAction = () => {
    setDateEnd( dayjs() );
    setTimeEnd( dayjs() );
  };

  const handleSaveAction = async () => {
    if ( !disable ) {
      try {
        if ( action && action.id ) {
          const modifyAction = {
            id: action.id,
            dateStart: dateAndTimeToISO( dateStart, timeStart ),
            dateEnd: timeEnd ? dateAndTimeToISO( dateEnd, timeEnd ) : undefined,
            cannotAchieve: cannotAchieve ? true : false,
          } as ModifyAction;
          await ActionService.putActionsById( action?.id, modifyAction, token, dispatch );

          const newNotes = notes.filter( note => { return note.id == null && note.content != null && note.content != ''; } );
          const modifiedNotes = notes.filter( note => {
            return (
              note.id != null &&
              action.notes.some( nt => {
                return nt.id == note.id && nt.disabled != note.disabled;
              } ) );
          } );

          if ( newNotes.length > 0 || modifiedNotes.length > 0 ) {
            await Promise.all( [
              ...modifiedNotes.map( note => { return NoteService.putNotesById(
                note.id,
                { disabled: note.disabled } as ModifyNote,
                token,
                dispatch
              ); } ),
              ...newNotes.map( note => { return NoteService.postNote( {
                content: note.content,
                idAction: action.id,
                idUser: me?.id,
                disabled: note.disabled
              } as PostNote, token, dispatch ); } )
            ] );
          }
        }
        else {
          const createAction = {
            idTask: task.id,
            idUser: user.id,
            dateStart: dateAndTimeToISO( dateStart, timeStart ),
            dateEnd: timeEnd ? dateAndTimeToISO( dateEnd, timeEnd ) : undefined,
            cannotAchieve: cannotAchieve
          } as PostAction;
          const newId = ( await ActionService.postAction( createAction, token, dispatch ) ).id;

          const newNotes = notes.filter( note => { return note.id == null && note.content != null && note.content != ''; } );
          if ( newNotes.length > 0 ) {
            await Promise.all(
              newNotes.map( note => { return NoteService.postNote( {
                content: note.content,
                idAction: newId,
                idUser: note.idUser,
                disabled: note.disabled
              } as PostNote, token, dispatch ); } )
            );
          }
        }
        if ( onChange ) {
          onChange( );
        }
      }
      catch ( error ) {
        console.error( 'Error saving action:', error );
      }
    }
  };

  const handleChangeDateStart = ( date: dayjs.Dayjs | undefined ) => {
    setDateStart( date ?? action?.dateStart ? dayjs( action?.dateStart ) : dayjs( task.dateStart ) );
  };

  const handleChangeDateEnd = ( date: dayjs.Dayjs | undefined ) => {
    setDateEnd( date ?? action?.dateStart ? dayjs( action?.dateStart ) : dayjs( task.dateStart ) );
  };

  return {
    dateStart,
    dateEnd,
    timeStart, setTimeStart,
    timeEnd, setTimeEnd,
    notes, setNotes, notesSave,
    images, setImages,
    cannotAchieve, setCannotAchieve,
    colorStatus,
    isFormValid,
    isFormChanged,
    errors,
    handleValidateAction, handleSaveAction,
    handleChangeDateStart, handleChangeDateEnd
  };
};

export default useActionTile;
